/**
 * InteractionRenderer - Renders interaction arrows and labels as SVG
 */

import type { InteractionLayout } from '../layout/types';

/**
 * Arrow path with label text for rendering
 */
export interface ArrowPath extends InteractionLayout {
  /**
   * Label text to display on the arrow
   */
  label: string;
  /**
   * Whether this arrow should be dimmed (optional)
   */
  isDimmed?: boolean;
}

export interface InteractionRenderOptions {
  /**
   * Size of the arrowhead
   */
  arrowheadSize: number;
  /**
   * CSS class for solid arrows
   */
  solidClass: string;
  /**
   * CSS class for dashed arrows
   */
  dashedClass: string;
  /**
   * CSS class for async arrows
   */
  asyncClass: string;
  /**
   * Vertical offset for message labels
   */
  labelOffsetY: number;
}

/**
 * Default rendering options for interactions
 */
export const DEFAULT_INTERACTION_OPTIONS: InteractionRenderOptions = {
  arrowheadSize: 8,
  solidClass: 'arrow-solid',
  dashedClass: 'arrow-dashed',
  asyncClass: 'arrow-async',
  labelOffsetY: -5,
};

/**
 * Gets the CSS class for an arrow based on its type
 */
function getArrowClass(
  arrowType: string,
  options: InteractionRenderOptions
): string {
  switch (arrowType) {
    case '->':
      return options.solidClass;
    case '-->':
      return options.dashedClass;
    case '->>':
      return options.asyncClass;
    case '-->>':
      return `${options.dashedClass} ${options.asyncClass}`;
    default:
      return options.solidClass;
  }
}

/**
 * Gets the stroke-dasharray attribute for an arrow type
 */
function getStrokeDashArray(arrowType: string): string {
  if (arrowType.startsWith('--')) {
    return '5,5';
  }
  return 'none';
}

/**
 * Renders an arrowhead marker definition
 */
export function renderArrowheadMarker(
  id: string,
  size: number = DEFAULT_INTERACTION_OPTIONS.arrowheadSize
): string {
  return (
    `<marker id="${id}" markerWidth="${size}" markerHeight="${size}" ` +
    `refX="${size}" refY="${size / 2}" orient="auto" markerUnits="userSpaceOnUse">` +
    `<polygon points="0,0 ${size},${size / 2} 0,${size}" class="arrowhead" />` +
    `</marker>`
  );
}

/**
 * Renders an interaction arrow between two participants
 */
export function renderArrow(
  arrow: ArrowPath,
  options: InteractionRenderOptions = DEFAULT_INTERACTION_OPTIONS
): string {
  const { startX, startY, endX, endY, label, arrowType, isDimmed } = arrow;
  const arrowClass = getArrowClass(arrowType, options);
  const strokeDashArray = getStrokeDashArray(arrowType);

  // Determine marker ID based on arrow type
  const markerId = arrowType.includes('>>') ? 'arrowhead-gt' : 'arrowhead-';

  const parts: string[] = [];

  // Wrap in a group with dimmed class if needed
  const groupClass = isDimmed
    ? 'interaction-arrow dimmed'
    : 'interaction-arrow';
  parts.push(`<g class="${groupClass}">`);

  // Check if this is a self-interaction (from and to are same participant)
  const isSelfInteraction = Math.abs(startX - endX) < 1;

  if (isSelfInteraction) {
    // Render self-interaction as a loop
    const loopWidth = 40;
    const loopHeight = 30;
    const startLoopX = startX + 5;
    const endLoopX = startX + 5;

    // Create a path that goes out and comes back
    // M = move to, Q = quadratic bezier curve
    const pathD =
      `M ${startLoopX},${startY} ` +
      `Q ${startX + loopWidth},${startY - loopHeight / 2} ${startX + loopWidth},${startY} ` +
      `Q ${startX + loopWidth},${startY + loopHeight / 2} ${endLoopX},${endY}`;

    parts.push(
      `  <path d="${pathD}" ` +
        `class="${arrowClass}" stroke-dasharray="${strokeDashArray}" ` +
        `marker-end="url(#${markerId})" fill="none" />`
    );

    // Position label to the right of the loop
    const labelX = startX + loopWidth + 10;
    const labelY = startY;

    parts.push(
      `  <text x="${labelX}" y="${labelY}" class="arrow-label" ` +
        `text-anchor="start">${label}</text>`
    );
  } else {
    // Regular interaction - draw straight line
    parts.push(
      `  <line x1="${startX}" y1="${startY}" x2="${endX}" y2="${endY}" ` +
        `class="${arrowClass}" stroke-dasharray="${strokeDashArray}" ` +
        `marker-end="url(#${markerId})" />`
    );

    // Draw the label
    const labelX = (startX + endX) / 2;
    const labelY = startY + options.labelOffsetY;

    parts.push(
      `  <text x="${labelX}" y="${labelY}" class="arrow-label" ` +
        `text-anchor="middle">${label}</text>`
    );
  }

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Renders multiple interactions for a step
 */
export function renderInteractions(
  arrows: ArrowPath[],
  stepIndex: number,
  options: InteractionRenderOptions = DEFAULT_INTERACTION_OPTIONS
): string {
  if (arrows.length === 0) {
    return '';
  }

  const parts: string[] = [];

  parts.push(`<g class="step-interactions" data-step="${stepIndex}">`);

  for (const arrow of arrows) {
    parts.push('  ' + renderArrow(arrow, options));
  }

  parts.push('</g>');

  return parts.join('\n');
}

/**
 * Renders all arrowhead marker definitions
 */
export function renderArrowheadMarkers(
  size: number = DEFAULT_INTERACTION_OPTIONS.arrowheadSize
): string {
  const parts: string[] = [];

  parts.push('<defs>');
  parts.push('  ' + renderArrowheadMarker('arrowhead-', size)); // Solid
  parts.push('  ' + renderArrowheadMarker('arrowhead-gt', size)); // Async
  parts.push('</defs>');

  return parts.join('\n');
}
